import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useHistory, useLocation } from 'react-router-dom';
import { H1, P, Spinner } from 'shared';
import { InfoBox } from 'components';
import { usePostServicingV1AccountsLenderAccountIdentifierBankAccountsManualMutation } from 'servicing-api';
import { toastError, toastSuccess } from 'utils';
import { BANK_ACCOUNT_PARAM, CHANGE_BANK_ACCOUNT_PARAM } from '../../constants';
import { useAuthorizationToken, useFlexFinalizeData, useFlowPath, } from '../../hooks';
import { getPostBankConnectionRoute, isChangingBankAccount, isPlaidFallback, } from '../../utils';
import { AddBankAccountForm } from '../forms';
const SUBMIT_ERRORS_BY_CODE = {
    unknown_routing_number: {
        routingNumber: 'We could not find a bank for that routing number.',
    },
    bank_account_already_added: {
        accountNumber: 'That bank account has already been added.',
    },
};
const errorCodeOf = (error) => error?.data?.error_code;
function AddBankAccount() {
    const history = useHistory();
    const location = useLocation();
    const isChangingAccount = isChangingBankAccount(location.search);
    const isAfterPlaidFailure = isPlaidFallback(location.search);
    const authorizationToken = useAuthorizationToken();
    const flowPath = useFlowPath(authorizationToken);
    const { lenderAccountIdentifier, finalizeData, isLoading } = useFlexFinalizeData(authorizationToken);
    const [postManualBankAccount] = usePostServicingV1AccountsLenderAccountIdentifierBankAccountsManualMutation();
    const handleSubmit = async (values) => {
        if (!lenderAccountIdentifier)
            return undefined;
        try {
            const created = await postManualBankAccount({
                lenderAccountIdentifier,
                postServicingV1AccountsLenderAccountIdentifierBankAccountsManual: {
                    routingNumber: values.routingNumber,
                    accountNumber: values.accountNumber,
                    accountType: values.accountType,
                    accountName: values.accountName,
                },
            }).unwrap();
            const nextRoute = isChangingAccount
                ? '/autopay'
                : getPostBankConnectionRoute(finalizeData);
            if (isChangingAccount)
                toastSuccess('Bank Account Updated');
            history.push(flowPath(nextRoute, created.id ? { [BANK_ACCOUNT_PARAM]: String(created.id) } : undefined));
            return undefined;
        }
        catch (error) {
            const fieldErrors = SUBMIT_ERRORS_BY_CODE[errorCodeOf(error) ?? ''];
            if (!fieldErrors) {
                toastError('Unable to save your bank account. Please try again.');
            }
            return fieldErrors;
        }
    };
    const handleBack = () => history.push(flowPath('/autopay/connect', isChangingAccount ? { [CHANGE_BANK_ACCOUNT_PARAM]: '1' } : undefined));
    if (isLoading || !lenderAccountIdentifier || !finalizeData) {
        return (_jsx("div", { className: "text-center", children: _jsx(Spinner, {}) }));
    }
    return (_jsxs("div", { className: "add-bank-account", children: [_jsx(H1, { variant: "headline-large", children: "Add Bank Account Details" }), _jsx(P, { variant: "body-large", className: "mar-t-8", children: "Enter your routing and account information to link this account." }), isAfterPlaidFailure && (_jsx(InfoBox, { className: "mar-t-16", children: _jsx(P, { variant: "body-medium", className: "mar-b-0", children: "We couldn't connect to your bank automatically. Enter your account details below instead." }) })), _jsx(AddBankAccountForm, { initialValues: {
                    accountType: 'checking',
                    accountName: [finalizeData.firstName, finalizeData.lastName]
                        .filter(Boolean)
                        .join(' '),
                }, handleSubmit: handleSubmit, onBack: handleBack })] }));
}
export default AddBankAccount;
